import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { API_URL as API } from '../config';
import { Layout } from '../components/Layout';
import SEO from '../components/SEO';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { MapPin, Mail, Calendar, MessageCircle, ChevronLeft, Lock } from 'lucide-react';
import { DisabilityBadge } from '../components/DisabilityBadge';
import { ConnectButton } from '../components/ConnectButton';
import { FollowButton } from '../components/FollowButton';
import { UserActionsDropdown } from '../components/UserActionsDropdown';
import { PeopleYouMayKnow } from '../components/PeopleYouMayKnow';

export default function MemberProfile() {
    const { userId } = useParams();
    const { user: currentUser } = useAuth();
    const navigate = useNavigate();
    const [member, setMember] = useState(null);
    const [loading, setLoading] = useState(true);

    const fetchMember = useCallback(async () => {
        setLoading(true);
        try {
            const response = await axios.get(`${API}/users/${userId}`);
            setMember(response.data);
        } catch (error) {
            console.error('Failed to fetch member:', error);
        } finally {
            setLoading(false);
        }
    }, [userId]);

    useEffect(() => {
        if (currentUser && userId === currentUser.id) {
            navigate('/profile');
            return;
        }
        fetchMember();
    }, [userId, currentUser, fetchMember, navigate]);

    if (loading) return <Layout><div className="flex items-center justify-center min-h-screen">Loading...</div></Layout>;
    if (!member) return <Layout><div className="flex items-center justify-center min-h-screen">User not found</div></Layout>;

    return (
        <Layout>
            <SEO title={`${member.name}'s Profile`} description={`Connect with ${member.name} on MyEnAb.`} />
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                <Button variant="ghost" onClick={() => navigate(-1)} className="mb-6 text-zinc-400 hover:text-white">
                    <ChevronLeft className="w-4 h-4 mr-2" /> Back
                </Button>

                <div className="flex flex-col lg:flex-row gap-8">
                    <div className="flex-grow">
                        <Card className="bg-[#18181B] border-[#27272A] mb-8 overflow-hidden">
                            <div className="h-32 inclusion-gradient-bg relative">
                                <div className="absolute -bottom-12 left-8">
                                    <div className="w-24 h-24 rounded-2xl bg-gradient-to-br from-inclusion-red via-inclusion-gold to-inclusion-green flex items-center justify-center border-4 border-[#18181B]">
                                        <span className="text-3xl font-bold text-black">{member.name?.charAt(0).toUpperCase()}</span>
                                    </div>
                                </div>
                            </div>
                            <CardContent className="pt-16 pb-6 px-8">
                                <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                                    <div>
                                        <h1 className="font-lexend text-2xl font-bold mb-1">{member.name}</h1>
                                        <p className="text-zinc-400 text-sm">{member.user_type?.replace('_', ' ')}</p>
                                        {member.organization_name && (
                                            <p className="text-zinc-300 text-sm mt-1">{member.organization_name}</p>
                                        )}
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <FollowButton userId={member.id} />
                                        <ConnectButton userId={member.id} />
                                        <UserActionsDropdown userId={member.id} userName={member.name} />
                                    </div>
                                </div>

                                <div className="flex flex-wrap items-center gap-4 mt-6 text-sm text-zinc-400">
                                    {member.location && (
                                        <span className="flex items-center gap-2">
                                            <MapPin className="w-4 h-4" />
                                            {member.location}
                                        </span>
                                    )}
                                    {member.created_at && (
                                        <span className="flex items-center gap-2">
                                            <Calendar className="w-4 h-4" />
                                            Joined {new Date(member.created_at).toLocaleDateString('en', { month: 'long', year: 'numeric' })}
                                        </span>
                                    )}
                                </div>

                                {member.disability_categories?.length > 0 && (
                                    <div className="flex flex-wrap gap-2 mt-6">
                                        {member.disability_categories.map(cat => (
                                            <DisabilityBadge key={cat} category={cat} />
                                        ))}
                                    </div>
                                )}
                                
                                {member.is_private && member.bio === "This profile is private." && (
                                    <div className="mt-6 p-4 bg-zinc-900/50 border border-zinc-800 rounded-lg flex items-center gap-3 text-zinc-400">
                                        <Lock className="w-5 h-5 text-inclusion-gold" />
                                        <p className="text-sm">Connect with {member.name} to see their full profile and disability details.</p>
                                    </div>
                                )}
                            </CardContent>
                        </Card>

                        {!(member.is_private && member.bio === "This profile is private.") && (
                            <Card className="bg-[#18181B] border-[#27272A]">
                                <CardContent className="p-8">
                                    <h2 className="font-lexend text-xl font-bold mb-4">About</h2>
                                    <p className="text-zinc-300 leading-relaxed">
                                        {member.bio || "No bio available."}
                                    </p>
                                </CardContent>
                            </Card>
                        )}
                    </div>

                    <aside className="w-full lg:w-80 space-y-8">
                        <PeopleYouMayKnow />
                    </aside>
                </div>
            </div>
        </Layout>
    );
}
